import React from "react"

export enum GroupingType {
  day = 'day',
  week = 'week',
  month = 'month',
}

export const thReachedColor = 'rgba(250,0,54,0.75)'
export const thNearbyColor = 'rgba(250,179,0,0.75)'
export const thTopColor = 'rgba(143,188,107,0.75)'
const greyColor = '#e2e2e2'

export const lowerIcon = <svg focusable="false" aria-hidden="true"
                              viewBox="0 0 24 24">
  <path d="m7 10 5 5 5-5z"></path>
</svg>
export const higherIcon = <svg focusable="false"
                               aria-hidden="true" viewBox="0 0 24 24">
  <path
    d="m7 14 5-5 5 5z"></path>
</svg>

export const lineChartConfig = (maxY?: number) => ({
  type: 'line',
  options: {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false
    },
    scales: {
      y: {
        type: 'linear',
        beginAtZero: true,
        max: maxY
      }
    },
    legend: {
      position: 'top',
    },
    title: {
      display: false,
    }
  },
})

export const lineChartConfigWithThreshold = (th: number, thTitle: string) => ({
  type: 'line',
  options: {
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false
    },
    plugins: {
      annotation: {
        annotations: {
          threshold: {
            type: 'line',
            yMin: th,
            yMax: th,
            borderColor: thReachedColor,
            borderWidth: 2,
            borderDash: [8, 4],
            label: {
              display: true,
              content: thTitle,
              backgroundColor: thReachedColor,
              color: 'white',
              position: 'end'
            }
          }
        }
      }
    },
    legend: {
      position: 'top',
    },
    title: {
      display: false,
    }
  },
})

export const barChartHorizontalConfig = (title: string, max: number) => ({
  type: 'bar',
  options: {
    indexAxis: 'y',
    animations: {
      numbers: {
        easing: 'easeOutCirc',
        duration: 800,
      }
    },
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        min: 0,
        max: max,
        grid: {
          color: greyColor,
        },
        border: {
          color: greyColor
        },
        ticks: {
          callback: v => Math.abs(v),
          color: '#333',
          font: {
            size: 16,
            family: 'Avenir',
          }
        }
      },
      y: {
        grid: {
          display: false
        },
        border: {
          color: greyColor
        },
        ticks: {
          color: '#333',
          font: {
            size: 16,
            family: 'Avenir',
          }
        },
      }
    },
    plugins: {
      tooltip: {
        enabled: true,
        callbacks: {
          label(ctx) {
            const value = Math.abs(ctx.parsed.x ?? ctx.parsed)
            return `${ctx.dataset.label}: ${value}`
          }
        }
      },
      legend: {
        display: false,
        position: 'right',
        labels: {
          color: '#333',
          font: {
            size: 18,
            family: 'Avenir',
          },
          boxWidth: 20,
          padding: 25
        }
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 18,
          family: 'Avenir',
        }
      }
    }
  },
})

export const barChartConfig = {
  type: 'bar',
  options: {
    animations: {
      numbers: {
        easing: 'easeOutCirc',
        duration: 800,
      }
    },
    responsive: true,
    maintainAspectRatio: true,
    scales: {
      x: {
        grid: {
          display: false
        },
        border: {
          color: greyColor
        },
        ticks: {
          color: '#333',
          font: {
            size: 16,
            family: 'Avenir',
          }
        }
      },
      y: {
        // type: 'logarithmic',
        grid: {
          color: greyColor,
        },
        border: {
          color: greyColor
        },
        ticks: {
          color: '#333',
          font: {
            size: 16,
            family: 'Avenir',
          }
        },
      }
    },
    plugins: {
      tooltip: {
        enabled: true
      },
      legend: {
        labels: {
          color: '#333',
          font: {
            size: 18,
            family: 'Avenir',
          },
          boxWidth: 20,
          padding: 25
        }
      },
    }
  },
}
export const lineChartConfigWithZone = (
  yFrom: number, lblFrom: string,
  yTo: number, lblTo: string
) => ({
  type: 'line',

  options: {
    responsive: true,
    interaction: {mode: 'index', intersect: false},

    plugins: {
      annotation: {
        annotations: {
          range: {
            type: 'box',
            yMin: Math.min(yFrom, yTo),
            yMax: Math.max(yFrom, yTo),
            backgroundColor: 'rgba(250,179,0,0.1)',
            borderWidth: 0,
          },

          fromLine: {
            type: 'line',
            yMin: yFrom,
            yMax: yFrom,
            borderColor: thNearbyColor,
            borderWidth: 2,
            borderDash: [8, 4],
            label: {
              display: true,
              content: lblFrom,
              backgroundColor: thNearbyColor,
              color: '#fff',
              position: 'end',
            },
          },
          toLine: {
            type: 'line',
            yMin: yTo,
            yMax: yTo,
            borderColor: thNearbyColor,
            borderWidth: 2,
            borderDash: [8, 4],
            label: {
              display: true,
              content: lblTo,
              backgroundColor: thNearbyColor,
              color: '#fff',
              position: 'end',
            },
          },
        },
      },
    },

    legend: {
      position: 'top',
    },
    title: {
      display: false,
    },
  },
})

export const numberComparison = (prevNumber: number | undefined, currentNumber: number, postfix = '', lowerIsBetter = false) => {
  const decrease = () => <span className="lower">{lowerIcon}{currentNumber}{postfix}</span>
  const increase = () => <span className="higher">{higherIcon}{currentNumber}{postfix}</span>

  if (prevNumber) {
    if (prevNumber > currentNumber) {
      return lowerIsBetter ? increase() : decrease()
    } else if (prevNumber < currentNumber) {
      return lowerIsBetter ? decrease() : increase()
    }
  }
  return <span className="same">{currentNumber}{postfix}</span>
}
